import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  activationFromError,
  offboardingFromError,
  signersText,
  type ActivationGate,
  type ActivationSummary,
  type OffboardingGate,
} from '../lib/api'
import { MOVE_ACTION_ID, mayMove, vendorLifecycle, vendorTransition, type LifecycleMove, type VendorLifecycle } from '../lib/lifecycle'
import { ErrorBox } from './DataTable'
import { GateChecklist } from './VendorActivation'
import { OffboardingChecklist } from './VendorOffboarding'
import { exitChainFromError, type ExitChain } from '../lib/exit'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { openRequestDrawer } from '../lib/requestDrawer'
import { vendorStage } from '../lib/status'
import '../styles/sweep.css'

// Where a vendor is in its life, and what may be done about it.
//
// Nothing here decides what is allowed. The server lists the moves for the
// vendor's current status, says which of them this person cannot make and why,
// and the control renders that. It holds no copy of the transition table, so
// the button a reader sees and the rule the API enforces cannot disagree.
//
// Until this existed there was no way to approve a vendor from the console at
// all. The endpoints had shipped; the pill that showed the status was only a
// pill.
//
// THE APPROVE BUTTON NOW HAS TWO REASONS TO BE GREY
//
// Maker-checker, which was the only one, and the activation gate: no tier, no
// scored assessment, an unsettled finding, no contract on file, or a stage of
// the approval matrix nobody has signed. Both arrive in blockedBecause, because
// the lifecycle read runs the same evaluation the transition enforces. This
// control adds no rule of its own; it renders the summary beside the button and
// offers the way to the checklist that carries the detail.
//
// TWO APPROVALS, NAMED APART (RELEASE 3.3, C3 OF THE 12 OCT PLAN)
//
// "Approve request" is the TPRM lead's decision at the start, made in the
// Approve request drawer; "Approve vendor" is this control's, the risk decision
// at the end. While the request waits, its Approve and Reject are the drawer's,
// so this control offers the drawer and not a second Reject.

const LABEL: Record<string, string> = {
  submit: 'Submit for approval',
  return: 'Return for more work',
  approve: 'Approve vendor',
  reject: 'Reject',
  suspend: 'Suspend',
  resume: 'Resume',
  // The exit's verbs are one set everywhere (release 3.2, removal item 8):
  // Propose, Start, Sign, Close. "Begin offboarding" and "Confirm offboarding
  // complete" were two more names for Start and Close.
  offboard: 'Start offboarding',
  offboarded: 'Close offboarding',
}

const GATE_REASON_ID = 'lifecycle-gate-reason'

// The lifecycle read carries this beside the moves. It is typed here rather
// than in lib/lifecycle.ts because that client is the transition table and this
// is the gate, and the two are separate answers that happen to travel together.
type LifecycleWithGate = VendorLifecycle & { activation?: ActivationSummary }

export function LifecycleControl({
  vendorId,
  onChanged,
  onOpenActivation,
  onOpenExit,
  only,
  except,
  questionnaireSent = null,
}: {
  vendorId: string
  // Whether a questionnaire has gone to the vendor, as the vendor header reads
  // it (assessments on record). Without it a Requested vendor whose request is
  // approved would read "In due diligence" here and "Approved for due
  // diligence" in the header (review of 12 Oct, S6).
  questionnaireSent?: boolean | null
  onChanged: () => void
  // Which moves this placement offers. The exit moves are offered on the Exit
  // tab (only) and left off the profile (except), so each move has one door.
  only?: string[]
  except?: string[]
  // Given where the exit moves are left off, so the reader is told where they went.
  onOpenExit?: () => void
  // Given by a screen that has somewhere to show the whole checklist. Without
  // it the outstanding conditions are still named, in the API's own words, on
  // the greyed button itself.
  onOpenActivation?: () => void
}) {
  const [life, setLife] = useState<LifecycleWithGate | null>(null)
  const [error, setError] = useState<unknown>(null)
  // A refused move is not a broken screen. It is kept apart from the load
  // failure above so the buttons stay where they were and the reader can act on
  // what the refusal said rather than reloading to get them back.
  const [moveError, setMoveError] = useState<ApiError | null>(null)
  const [refusedGate, setRefusedGate] = useState<ActivationGate | null>(null)
  // The other refusal that carries its whole evaluation. Until this was read, a
  // refused offboarding showed the sentence naming eleven outstanding items and
  // none of the items themselves, because only body.activation was looked at.
  const [refusedExit, setRefusedExit] = useState<OffboardingGate | null>(null)
  // And the exit chain, which a refused close carries beside the checklist:
  // the stages nobody has signed, each with the roles that sign it.
  const [refusedChain, setRefusedChain] = useState<ExitChain | null>(null)
  const [pending, setPending] = useState<LifecycleMove | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  // Submit's way past the baseline rule (release 3.3): asked for once the
  // server has said the tier's questionnaire is not scored.
  const [override, setOverride] = useState('')

  const load = useCallback(() => {
    vendorLifecycle(vendorId)
      .then((l) => {
        setLife(l as LifecycleWithGate)
        setError(null)
      })
      .catch(setError)
  }, [vendorId])

  useEffect(load, [load])

  async function perform(move: LifecycleMove, withOverride = '') {
    if (move.requiresReason && reason.trim() === '') {
      setPending(move)
      return
    }
    setBusy(true)
    setMoveError(null)
    setRefusedGate(null)
    try {
      await vendorTransition(vendorId, move.action, reason.trim(), withOverride.trim())
      setPending(null)
      setReason('')
      setOverride('')
      load()
      onChanged()
    } catch (err) {
      const e = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
      setMoveError(e)
      // A blocked activation arrives with the whole evaluation attached, so the
      // refusal is rendered as the checklist the reader would have seen before
      // pressing rather than as a code and a paragraph.
      setRefusedGate(activationFromError(e))
      setRefusedExit(offboardingFromError(e))
      setRefusedChain(exitChainFromError(e))
    } finally {
      setBusy(false)
    }
  }

  if (error != null) return <ErrorBox error={error} what="The lifecycle" />
  if (life == null) return <span className="muted">checking what can be done</span>

  // Only the moves this reader's roles may make (release 3.1). The API leaves
  // the others out already; filtering again here keeps a read-only reader from
  // being offered a button whose POST answers 403 on an older API.
  // While the request waits, its decision is the Approve request drawer's:
  // the reject move here is the vendor's, not the request's.
  const requested = life.status === 'DRAFT' && life.request?.approved === false
  const mine = life.moves.filter((m) => mayMove(m.action) && !(requested && m.action === 'reject'))
  const offered = mine.filter((m) => (!only || only.includes(m.action)) && !(except ?? []).includes(m.action))
  const mayDecideRequest = requested && !only && life.request?.approve != null
  const submit = life.moves.find((m) => m.action === 'submit')
  const elsewhere = except ? mine.filter((m) => except.includes(m.action)) : []
  const anyBlocked = offered.some((m) => m.blockedBecause)
  const gate = life.activation
  // The activation gate is about approving, which the exit placement never offers.
  const gateHolds = !only && gate != null && !gate.met

  if (only && offered.length === 0 && pending == null && !moveError) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        {life.status === 'ACTIVE' || life.status === 'SUSPENDED'
          ? 'Somebody else who runs offboarding can begin it'
          : 'No offboarding move is open at ' + life.status.replace(/_/g, ' ').toLowerCase()}
      </p>
    )
  }

  // The same word and tone the vendor header uses, so the two cannot name one
  // status differently.
  const word = vendorStage(life.status, questionnaireSent, life.request?.approved)

  return (
    <div className="lifecycle">
      {!only && <Status tone={word.tone}>{word.word}</Status>}
      {!only && life.statusSince != null && (
        <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
          since {life.statusSince.slice(0, 10)}
        </span>
      )}

      {mayDecideRequest && (
        <div className="chiprow" style={{ marginTop: 8 }}>
          <button type="button" className="primary" onClick={() => openRequestDrawer(vendorId)}>
            Approve request
          </button>
        </div>
      )}

      {offered.length === 0 ? null : (
        <div className="chiprow" style={{ marginTop: only ? 0 : 8 }}>
          {offered.map((m) => (
            <span key={m.action} className="chiprow">
              <button
                type="button"
                className={m.action === 'approve' ? 'primary' : undefined}
                disabled={busy || Boolean(m.blockedBecause)}
                title={m.blockedBecause || m.why}
                data-action={MOVE_ACTION_ID[m.action]}
                aria-describedby={m.action === 'approve' && gateHolds ? GATE_REASON_ID : undefined}
                onClick={() => void perform(m)}
              >
                {LABEL[m.action] ?? m.action}
              </button>
              {only && m.blockedBecause && (
                <span className="muted" style={{ fontSize: 12 }} title={m.blockedBecause}>
                  {m.blockedBecause.length > 80 ? m.blockedBecause.slice(0, 77).trimEnd() + '...' : m.blockedBecause}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {elsewhere.length > 0 && onOpenExit && (
        <p className="muted sw-row" style={{ margin: '6px 0 0', fontSize: 12 }}>
          <span>
            {elsewhere.map((m) => LABEL[m.action] ?? m.action).join(' and ')}{' '}
            {elsewhere.length === 1 ? 'is' : 'are'} on the Offboarding tab
          </span>
          <Toggletip label="Why it is on the Offboarding tab">
            Offboarding moves are made on the Offboarding tab, with the chain they need.
          </Toggletip>
          <button type="button" className="linkish" onClick={onOpenExit}>
            Open the Offboarding tab
          </button>
        </p>
      )}

      {/* The count leads; which band, tier, matrix and round the conditions
          come from is behind the (i), because it matters when the count is
          questioned rather than every time the profile is read. */}
      {gateHolds && (
        <p className="muted sw-row" style={{ margin: '6px 0 0', fontSize: 12 }} id={GATE_REASON_ID}>
          <Status tone="warn">Activation held</Status>
          <span>{gate.outstanding === 1 ? '1 condition' : gate.outstanding + ' conditions'} outstanding</span>
          <Toggletip label="Which chain and conditions apply">
            {'The chain and the conditions are the ones for band ' +
              gate.band +
              ' at ' +
              (gate.tierLabel || 'its tier') +
              ', under approval matrix v' +
              gate.matrixVersionNo +
              ', round ' +
              gate.round +
              '.'}
          </Toggletip>
          {onOpenActivation && (
            <button type="button" className="linkish" onClick={onOpenActivation}>
              Open the activation checklist
            </button>
          )}
        </p>
      )}

      {/* On the exit workspace a greyed move carries its reason as its title
          and as the line beside it, so the general line is left off. */}
      {anyBlocked && !only && (
        <p className="muted sw-row" style={{ margin: '6px 0 0', fontSize: 12 }}>
          <span>A greyed step names its own reason</span>
          <Toggletip label="Why a step is greyed">
            {'Whoever last acted on a vendor cannot be the one who approves the next step, whatever their role.' +
              (gateHolds ? ' Approval waits for the conditions above as well.' : '')}
          </Toggletip>
        </p>
      )}

      {moveError && (
        <div style={{ marginTop: 10 }}>
          <ErrorBox error={moveError} what="This vendor" verb="moved" />
          {refusedGate && <GateChecklist gate={refusedGate} />}
          {refusedChain && refusedChain.unsigned.length > 0 && (
            <ul className="act-list">
              {refusedChain.unsigned.map((s) => (
                <li key={s.key} className="act-item act-unmet">
                  <div className="act-head">
                    {s.state === 'declined' ? (
                      <Status tone="crit">Declined</Status>
                    ) : (
                      <Status tone="warn">Unsigned</Status>
                    )}
                    <strong>{s.job}</strong>
                  </div>
                  {/* The decliner's note is theirs, of any length, so it is kept
                      word for word behind the (i) beside who declined. */}
                  <p className="act-detail sw-row">
                    {s.state === 'declined' ? (
                      <>
                        <span>{'Declined by ' + (s.signedBy ?? '')}</span>
                        <ReasonTip label="Why it was declined" text={s.note} />
                      </>
                    ) : (
                      'Signed by ' + signersText(s.roles, s.roleNames)
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {refusedExit && <OffboardingChecklist gate={refusedExit} />}
          {moveError.code === 'BASELINE_NOT_SCORED' && submit && (
            <div className="panel" style={{ marginTop: 10 }}>
              <label htmlFor="lifecycle-override">Why it goes forward without it</label>
              <p className="sw-help" id="lifecycle-override-note">
                Recorded on the submission, and read by every approver after
              </p>
              <textarea
                id="lifecycle-override"
                aria-describedby="lifecycle-override-note"
                rows={3}
                maxLength={1000}
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              />
              <div className="chiprow" style={{ marginTop: 8 }}>
                <button type="button" className="primary" disabled={busy || override.trim() === ''} onClick={() => void perform(submit, override)}>
                  Submit with this reason
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {pending != null && (
        <div className="panel" style={{ marginTop: 10 }}>
          <label htmlFor="lifecycle-reason">Why?</label>
          <p className="sw-help" id="lifecycle-reason-note">
            Recorded against the change, and shown to whoever asks
          </p>
          <textarea
            id="lifecycle-reason"
            aria-describedby="lifecycle-reason-note"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', marginTop: 4 }}
          />
          <div className="chiprow" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="primary"
              disabled={busy || reason.trim() === ''}
              onClick={() => void perform(pending)}
            >
              {LABEL[pending.action] ?? pending.action}
            </button>
            <button type="button" disabled={busy} onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
